import { useUser } from "@clerk/clerk-expo";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { ToastViewport } from "@tamagui/toast";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Slot, useGlobalSearchParams, usePathname } from "expo-router";
import { StatusBar } from "expo-status-bar";
import React, { useCallback, useContext, useEffect } from "react";
import {
  Button as RNButton,
  ScrollView as RNScrollView,
  Text as RNText,
  View as RNView,
} from "react-native";
import {
  ReduceMotion,
  ReducedMotionConfig,
  useAnimatedStyle,
} from "react-native-reanimated";

import { configureAxiosInterceptor } from "@/api/config";
import { createAnonymousUser } from "@/api/generated";
import { useRefreshAnonymousUser } from "@/api/mutations/user";
import { useGetUserProfile } from "@/api/queries/userProfile";
import { useSocket } from "@/api/sockets";
import {
  DevSidebar,
  FeatureFlagProvider,
  LayoutContext,
  LayoutProvider,
  LoadingSpinnerClipPath,
  ModalHost,
  QueryClientProvider,
  TamaguiAndThemeProvider,
  View,
  XStack,
  getTokenValue,
  useTheme,
} from "@/components";
import { SkipToLink } from "@/components/a11y";
import { AnimatedView } from "@/components/animated";
import ClerkProvider from "@/components/ClerkProvider";
import DataSharingRevokedOverlay from "@/components/DataSharingRevokedOverlay";
import { Main } from "@/components/Layout";
import LiveRegion from "@/components/LiveRegion";
import { NoriHeader } from "@/components/NoriHeader";
import Toast from "@/components/Toast";
import Under18BlockingOverlay from "@/components/Under18BlockingOverlay";
import { VisitStatusIndicatorPopup } from "@/components/VisitStatusIndicator";
import { isDev, isLocal } from "@/config/env";
import ZocdocBookingModal from "@/conversation/AssistantMessage/ZocdocBooking/ZocdocBookingModal";
import CuraiConversationModal from "@/conversation/CuraiConversationModal";
import RxQuestionnaire from "@/conversation/RxQuestionnaire";
import Sidebar from "@/conversation/sidebar/Sidebar";
import VideoCallContainer from "@/conversation/video/VideoCallContainer";
import {
  AnalyticsProvider,
  useAnalytics,
  useTrackPageLoad,
} from "@/hooks/analytics";
import { useCurrentConversationId } from "@/hooks/conversation";
import { useAuthState } from "@/hooks/useAuthState";
import useGetAuthToken from "@/hooks/useAuthToken";
import { useAxeAccessibility } from "@/hooks/useAxeAccessibility";
import { useBrowserHistoryBreadcrumb } from "@/hooks/useBrowserHistoryBreadcrumb";
import { useCampaignLink } from "@/hooks/useCampaignLink";
import { useFonts } from "@/hooks/useFonts";
import useInitializeSentryIntegrations from "@/hooks/useInitializeSentryIntegrations";
import { useInitializeUserCredentials } from "@/hooks/useInitializeUserCredentials";
import { useIsSignedInStable } from "@/hooks/useIsSignedInStable";
import useLogout from "@/hooks/useLogout";
import {
  partnerFromPathname,
  useCapturePartnerIntent,
} from "@/hooks/usePartnerIntent";
import { usePermissionsPolicyViolations } from "@/hooks/usePermissionsPolicyViolations";
import { useVideoCallManager } from "@/hooks/video";
import AgeVerificationDialog from "@/settings/ageVerification/AgeVerificationDialog";
import SettingsDialog, { SettingsTab } from "@/settings/dialog";
import { VideoModes, useAuthStore } from "@/store";
import CSATSurveyModal from "@/survey/CSATSurveyModal";
import { colors } from "@/theme/tokens";
import { identifyUser as identifyUserCustomerIO } from "@/utils/customerIO";
import { initSentry, setTag, withErrorBoundary } from "../sentry";

initSentry();

export const LayoutNeedingContext = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  const theme = useTheme();
  const { settings: activeTab } = useGlobalSearchParams<{
    settings: SettingsTab;
  }>();
  const getAccessToken = useGetAuthToken();
  const conversationId = useCurrentConversationId();
  const logout = useLogout();
  const { mutateAsync: refreshAnonymousTokenMutate } =
    useRefreshAnonymousUser();
  const refreshAnonymousToken = useCallback(async (): Promise<void> => {
    await refreshAnonymousTokenMutate();
  }, [refreshAnonymousTokenMutate]);
  const { setAnonymousUserId } = useAuthStore();
  const { userId, anonymousUserId } = useAuthState();
  const isSignedInStable = useIsSignedInStable();
  const { user, isLoaded: isUserLoaded } = useUser();
  const { data: userProfile } = useGetUserProfile();
  useSocket();
  const { identify } = useAnalytics();
  const { userResearchCohortTag } = useCampaignLink();
  const pathname = usePathname();
  const isMarketingPage = Boolean(partnerFromPathname(pathname));
  const showLandingPage =
    (!isSignedInStable && !conversationId) || isMarketingPage;

  useCapturePartnerIntent();

  // Get user credentials or create anonymous user if not authenticated
  // invalidate queries when auth is in loading state
  useInitializeUserCredentials();
  useInitializeSentryIntegrations();

  useTrackPageLoad();
  useBrowserHistoryBreadcrumb();
  usePermissionsPolicyViolations();

  const callObject = useCallObject({});
  const { videoMode, leaveCall } = useVideoCallManager(callObject);

  useEffect(() => {
    if (userId && isUserLoaded) {
      identify(userId, {
        phone: user?.primaryPhoneNumber?.phoneNumber,
        ...(userResearchCohortTag && { uxr_cohort: userResearchCohortTag }),
      });
      // this is needed for customer.io in-app messages
      identifyUserCustomerIO(userId);
      setTag("user_id", userId);
    } else if (anonymousUserId) {
      identify({
        anonymousId: anonymousUserId,
        ...(userResearchCohortTag && { uxr_cohort: userResearchCohortTag }),
      });
      // this is needed for customer.io in-app messages
      identifyUserCustomerIO(anonymousUserId);
      setTag("user_id", anonymousUserId);
    }

    if (userResearchCohortTag) {
      setTag("uxr_cohort", userResearchCohortTag);
    }
  }, [
    identify,
    userId,
    anonymousUserId,
    isUserLoaded,
    user,
    userResearchCohortTag,
  ]);

  useEffect(() => {
    if (userProfile?.generalistRefactor) {
      setTag("generalist_refactor", true);
    }
  }, [userProfile?.generalistRefactor]);

  const createAnonymousSession = useCallback(async (): Promise<void> => {
    const response = await createAnonymousUser();
    if (response?.anonymous_user_id) {
      setAnonymousUserId(response.anonymous_user_id);
    }
  }, [setAnonymousUserId]);

  useEffect(() => {
    configureAxiosInterceptor(
      getAccessToken,
      refreshAnonymousToken,
      logout,
      createAnonymousSession,
    );
  }, [getAccessToken, refreshAnonymousToken, logout, createAnonymousSession]);

  const { lg, anims, windowWidth, sidebarState, toggleSidebar } =
    useContext(LayoutContext);
  const isSidebarOpen = ["expanding", "expanded"].includes(sidebarState);
  const hasAppChrome =
    (Boolean(isSignedInStable) || Boolean(conversationId)) &&
    (lg || isSidebarOpen);
  const contentGutter = hasAppChrome ? getTokenValue("$screenPadding") : 0;
  const contentPaddingRight = contentGutter;
  const contentPaddingLeft = isSidebarOpen ? contentGutter / 2 : contentGutter;
  const mainContentFrameWidth =
    windowWidth + contentPaddingLeft + contentPaddingRight;

  const contentTranslateStyle = useAnimatedStyle(() => {
    if (lg || showLandingPage) return { transform: [{ translateX: 0 }] };
    return { transform: [{ translateX: anims.sidebarWidth.value }] };
  }, [lg, showLandingPage, anims.sidebarWidth]);
  return (
    <DailyProvider callObject={callObject}>
      <AgeVerificationDialog />
      <Under18BlockingOverlay />
      <DataSharingRevokedOverlay />
      <SettingsDialog tab={activeTab} />
      <CuraiConversationModal />
      <RxQuestionnaire />
      <CSATSurveyModal />
      <ZocdocBookingModal />
      <ModalHost />
      <View
        style={{
          flex: 1,
          height: "100%",
          ...(!showLandingPage ? { position: "fixed" } : {}),
          backgroundColor: theme.backgroundGround?.get(),
          overflow: "hidden",
          width: !showLandingPage && !lg ? mainContentFrameWidth : "100%",
        }}
      >
        <SkipToLink />
        {(isLocal || isDev) && (
          <ReactQueryDevtools
            buttonPosition="top-right"
            initialIsOpen={false}
          />
        )}
        <XStack backgroundColor="$backgroundGround" flex={1} width="100%">
          <Sidebar visible={Boolean(isSignedInStable)} />
          {sidebarState === "collapsed" && <VisitStatusIndicatorPopup />}
          <AnimatedView
            // Padding pops instantly on toggle
            paddingLeft={contentPaddingLeft}
            paddingRight={contentPaddingRight}
            paddingVertical={contentGutter}
            width={mainContentFrameWidth}
            $lg={{ flex: 1, width: "auto", flexDirection: "row" }}
            style={contentTranslateStyle}
            // On mobile, intercept presses to close the sidebar
            {...(!lg &&
              isSidebarOpen && {
                pointerEvents: "box-only",
                onPress: () => toggleSidebar(),
                "aria-label": "Press to close sidebar",
              })}
          >
            <View flex={1} width="100%" $lg={{ width: "auto" }}>
              <NoriHeader />
              <Main id="main" tabIndex={-1} flex={1}>
                {children ?? <Slot initialRouteName="index" />}
              </Main>
            </View>
            {(isLocal || isDev) && <DevSidebar />}
          </AnimatedView>
        </XStack>
        <StatusBar style="auto" />
      </View>
      {videoMode === VideoModes.FLOATING && (
        <VideoCallContainer mode={videoMode} leaveCall={leaveCall} />
      )}
    </DailyProvider>
  );
};

function TopLevelProviders() {
  useFonts();
  useAxeAccessibility();
  return (
    <>
      <ReducedMotionConfig mode={ReduceMotion.System} />
      <AnalyticsProvider>
        <FeatureFlagProvider>
          <ClerkProvider>
            <TamaguiAndThemeProvider>
              <QueryClientProvider>
                <LiveRegion />
                <Toast />
                <ToastViewport
                  flexDirection="column"
                  top={12}
                  left={0}
                  right={0}
                  zIndex={1000000}
                  portalToRoot
                />
                <LayoutProvider>
                  <LayoutNeedingContext />
                </LayoutProvider>
              </QueryClientProvider>
            </TamaguiAndThemeProvider>
          </ClerkProvider>
        </FeatureFlagProvider>
      </AnalyticsProvider>
      <LoadingSpinnerClipPath />
    </>
  );
}

export default withErrorBoundary(TopLevelProviders, {
  fallback: ({ error, componentStack, resetError }) => {
    // We use RN components here because we're likely not within Tamagui context.
    // this is what caused the preponderance of "check your tamagui versions match" messages
    return (
      <RNScrollView contentContainerStyle={{ gap: 16, margin: 24 }}>
        <RNView style={{ maxWidth: 300, margin: "auto", gap: 16 }}>
          <RNText style={{ textAlign: "center" }}>
            An error has occurred, please refresh the page, or
          </RNText>
          <RNButton
            color={colors.curai500}
            onPress={resetError}
            title="Try again?"
          />
        </RNView>
        {isLocal && (
          <>
            <RNText
              style={{
                fontWeight: "bold",
                textAlign: "center",
                color: colors.light_red,
              }}
            >
              {error instanceof Error ? error.message : "Unknown error"}
            </RNText>
            <RNText>{componentStack}</RNText>
          </>
        )}
      </RNScrollView>
    );
  },
});
